import type { FC, ReactNode } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Tooltip, Typography, useTheme } from '@mui/material'
import { STATUS_DOT_SIZE, STATUS_ICON_SIZE, STATUS_ICON_SIZE_COLLAPSED } from '../constants'
import { standardTooltipProps } from '@/common/components/tooltipStyles'

interface StatusIndicatorProps {
  readonly icon: ReactNode
  readonly label: string
  readonly statusColor: string
  readonly isCollapsed?: boolean
  readonly isPulsing?: boolean
  readonly isClickable?: boolean
  readonly onClick?: () => void
}

export const StatusIndicator: FC<StatusIndicatorProps> = ({
  icon,
  label,
  statusColor,
  isCollapsed = false,
  isPulsing = false,
  isClickable = false,
  onClick,
}) => {
  const theme = useTheme()
  const iconSize = isCollapsed ? STATUS_ICON_SIZE_COLLAPSED : STATUS_ICON_SIZE

  const statusIconContent = (
    <Box
      component={isClickable ? 'button' : 'div'}
      onClick={isClickable ? onClick : undefined}
      sx={iconContainerSx(isClickable)}
    >
      <Box sx={iconWrapperSx(iconSize)}>{icon}</Box>
      <Box sx={statusDotSx(statusColor, isPulsing, theme)} />
    </Box>
  )

  if (isCollapsed) {
    return (
      <Tooltip {...standardTooltipProps} title={label} placement="right">
        {statusIconContent}
      </Tooltip>
    )
  }

  return (
    <Box
      component={isClickable ? 'button' : 'div'}
      onClick={isClickable ? onClick : undefined}
      sx={expandedRowSx(isClickable)}
    >
      <Box sx={iconContainerSx(false)}>
        <Box sx={iconWrapperSx(iconSize)}>{icon}</Box>
        <Box sx={statusDotSx(statusColor, isPulsing, theme)} />
      </Box>
      <Typography variant="body2" sx={labelSx}>
        {label}
      </Typography>
    </Box>
  )
}

const iconContainerSx = (isClickable: boolean): SxProps<Theme> => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  padding: 0,
  cursor: isClickable ? 'pointer' : 'default',
  '&:hover': isClickable
    ? {
        '& .MuiSvgIcon-root': {
          color: 'text.primary',
        },
      }
    : {},
})

const iconWrapperSx = (size: number): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: size,
    color: 'text.disabled',
    opacity: 0.6,
    transition: 'color 200ms ease-in-out, opacity 200ms ease-in-out',
  },
})

const statusDotSx = (color: string, isPulsing: boolean, theme: Theme): SxProps<Theme> => ({
  position: 'absolute',
  bottom: -2,
  right: -2,
  width: STATUS_DOT_SIZE,
  height: STATUS_DOT_SIZE,
  borderRadius: '50%',
  backgroundColor: color,
  border: `1px solid ${theme.palette.background.paper}`,
  ...(isPulsing && {
    animation: 'statusDotPulse 1.5s ease-in-out infinite',
    '@keyframes statusDotPulse': {
      '0%, 100%': {
        opacity: 0.7,
        transform: 'scale(1)',
      },
      '50%': {
        opacity: 1,
        transform: 'scale(1.3)',
      },
    },
  }),
})

const expandedRowSx = (isClickable: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: isClickable ? 'pointer' : 'default',
  borderRadius: 1,
  transition: 'background-color 200ms ease-in-out',
  ...(isClickable && {
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  }),
})

const labelSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.875rem',
}
